/**
 * Check if a promise is successful
 */
export const isSuccessful = <T>(
  result: PromiseSettledResult<T>
): result is PromiseFulfilledResult<T> => {
  return result.status === 'fulfilled'
}

/**
 * Check if a promise is failed
 */
export const isFailed = <T>(result: PromiseSettledResult<T>): result is PromiseRejectedResult => {
  return result.status === 'rejected'
}

/**
 * sifts out the successful promises, returning only the values
 * and discarding the rejections
 */
export const siftSuccesses = async <T>(promises: Promise<T>[]): Promise<T[]> => {
  const results = await Promise.allSettled(promises)
  return results.filter(isSuccessful).map((p) => p.value)
}

/**
 * sifts out the failed promises,
 */
export const siftFailures = async <T>(promises: Promise<T>[]): Promise<Error[]> => {
  const results = await Promise.allSettled(promises)
  return results
    .filter(isFailed)
    .map((p) => (p.reason instanceof Error ? p.reason : new Error(p.reason)))
}

/**
 * Executes promises in parallel and then returns a promise that resolves to an object
 * with the same keys as the input object, but with the resolved values
 *
 * Similar to Promise.all but for objects, with the goal of improving readability
 *
 * @example
 * const { a, b } = await all({
 *  a: Promise.resolve(1),
 *  b: Promise.resolve('hello')
 * })
 */
export async function all<T extends Record<string, Promise<unknown>>>(
  promises: T
): Promise<{ [K in keyof T]: Awaited<T[K]> }> {
  const entries = Object.entries(promises)
  const results = await Promise.all(entries.map(([, promise]) => promise))
  return Object.fromEntries(entries.map(([key], index) => [key, results[index]])) as {
    [K in keyof T]: Awaited<T[K]>
  }
}

/**
 * Executes promises in parallel and then returns a promise that resolves to an object
 * with the same keys as the input object, but with the settled values
 * (either fulfilled or rejected)
 *
 * Similar to Promise.allSettled but for objects, with the goal of improving readability
 *
 * @example
 * const { a, b } = await allSettled({
 *   a: Promise.resolve(1),
 *   b: Promise.reject(new Error('hello'))
 * })
 *
 * if (a.status === 'fulfilled') {
 *   console.log(a.value)
 * } else {
 *   console.error(a.reason)
 * }
 */
export async function allSettled<T extends Record<string, Promise<unknown>>>(
  promises: T
): Promise<{ [K in keyof T]: PromiseSettledResult<Awaited<T[K]>> }> {
  const entries = Object.entries(promises)
  const results = await Promise.allSettled(entries.map(([, promise]) => promise))
  return Object.fromEntries(entries.map(([key], index) => [key, results[index]])) as {
    [K in keyof T]: PromiseSettledResult<Awaited<T[K]>>
  }
}

/**
 * Executes promises in parallel and then returns a promise that resolves to an array
 * with the resolved values. Rejects immediately if any promise rejects.
 *
 * Optionally, you can specify the maximum number of tasks to run concurrently.
 * The default is `Infinity` (i.e., running everything at once).
 *
 * For a synchronous version, see `mapSync`
 */
export async function mapConcurrent<T, U>(
  items: T[],
  mapper: (item: T, index: number) => Promise<U>,
  { concurrency = Number.POSITIVE_INFINITY, throwOnError = true } = {}
): Promise<U[]> {
  if (concurrency <= 0) {
    throw new Error('Concurrency must be a positive integer')
  }

  if (items.length === 0) {
    return []
  }

  if (items.length === 1) {
    return [await mapper(items[0], 0)]
  }

  /**
   * The results array. We will populate this on an index-by-index basis
   * as the promises resolve.
   */
  const results = Array.from<U>({ length: items.length })

  /**
   * The index of the next item to process.
   */
  let index = 0
  /**
   * The number of active tasks. Will be incremented when a task starts
   * and decremented when it finishes.
   */
  let active = 0
  let hasError = false

  /**
   * The `done` promise will resolve when all items have been processed.
   * We store its resolver function as `resolveFinal`, in the function scope,
   * so we can manually trigger it when all processing is complete and no
   * tasks remain active.
   */
  let resolveFinal: () => void
  const done = new Promise<void>((resolve) => (resolveFinal = resolve))

  let rejectFinal: (reason?: unknown) => void
  /**
   * Awaiting this will require either the `resolveFinal` or `rejectFinal`
   * function to be called.
   */
  const resultPromise = new Promise<U[]>((resolve, reject) => {
    rejectFinal = reject
    done.then(() => resolve(results))
  })

  const next = () => {
    if (hasError) {
      return
    }

    if (index >= items.length) {
      if (active === 0) resolveFinal()
      return
    }

    const currentIndex = index++ // store the current index (for lookup in both items and in results arrays) inside this closure
    active++ // we're starting a new task; it's now active

    mapper(items[currentIndex], currentIndex)
      .then((result) => {
        results[currentIndex] = result // store the result at the same index we got it from
      })
      .catch((err) => {
        if (throwOnError) {
          hasError = true // set flag so we can short-circuit the rest of the processing
          rejectFinal(err) // let the error bubble up by rejecting the outer promise
        }
      })
      .finally(() => {
        active-- // we finished processing this task; it's not active anymore
        next()
      })
  }

  // Start up to `concurrency` workers, each process will call `next()`
  // when it finishes processing its item, which will start the next one
  // until all items have been processed.
  for (let i = 0; i < Math.min(concurrency, items.length); i++) {
    next()
  }

  return resultPromise
}

/**
 * Executes promises in sequence and then returns a promise that resolves to an array
 * with the resolved values. Rejects immediately if any promise rejects.
 *
 * For an asynchronous version, see `mapConcurrent`
 */
export async function mapSync<T, U>(
  items: T[],
  mapper: (item: T, index: number) => Promise<U>
): Promise<U[]> {
  const results: U[] = []

  for (let i = 0; i < items.length; i++) {
    results.push(await mapper(items[i], i))
  }

  return results
}

/**
 * Calls a function after a specified number of milliseconds.
 * Returns a promise that resolves to the result of the function call.
 *
 * Essentially it promisifies setTimeout.
 */
export const callAfter = <T>(ms: number, onTimeout: () => T | Promise<T>): Promise<T> =>
  new Promise<T>((resolve) => {
    setTimeout(() => {
      resolve(onTimeout())
    }, ms)
  })
